<template>
    <div id="documentsPage" class="bg-gray-100 min-h-screen p-8">
        <h1 class="text-3xl font-bold text-gray-800 mb-6">Dökümanlar</h1>
        <div class="folder-container grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            <div v-for="folder in folders" :key="folder.id" @click="fetchFolderFiles(folder.id)" 
                 class="folder-item bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-4 flex flex-col items-center justify-center cursor-pointer relative"
                 :class="{'border-2 border-blue-500': selectedFolderId === folder.id}"
                 @contextmenu.prevent="showUpdateFolderPopup(folder)" style="tool"
                 >
                 
                <vs-icon icon="folder" size="large" :class="{'text-blue-500': selectedFolderId !== folder.id, 'text-blue-700': selectedFolderId === folder.id}" class="mb-3"></vs-icon>
                
                <span class="folder-name text-sm font-medium text-gray-700 text-center break-words w-full" :class="{'text-blue-700 font-semibold': selectedFolderId === folder.id}">{{ folder.name }}</span>
                <div class="flex mt-2" v-if="$isSuperAdmin()">
                            <vs-button class="mr-2" size="small" color="primary" @click="showUpdateFolderPopup(folder)">
                                <vs-icon icon="edit" class="mx-auto my-auto"></vs-icon>
                            </vs-button>
                            <vs-button size="small" color="danger" @click="confirmDeleteFolder(folder)">
                                <vs-icon icon="delete" class="mx-auto my-auto"></vs-icon>
                            </vs-button>
                        </div>
            </div>
            
            <div @click="showNewFolderPopup"  v-if="$isSuperAdmin()"
                 class="folder-item bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-4 flex flex-col items-center justify-center cursor-pointer">
                <vs-icon icon="add" size="large" class="text-blue-500 mb-3"></vs-icon>
                <span class="text-sm font-medium text-gray-700">Yeni Klasör</span>
            </div>
        </div>
        <div v-if="selectedFolderId !== null" class="mt-8">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
                Dosyalar - {{ selectedFolderName }}
            </h2>
            <div class="bg-white rounded-lg shadow-md p-6">
                <div v-if="selectedFolderFiles && selectedFolderFiles.length > 0" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <div v-for="file in selectedFolderFiles" :key="file.id" 
                         class="file-item flex flex-col items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors duration-200 relative min-w-[200px]"
                         v-tooltip="file.description">
                        <vs-icon icon="description" size="large" class="text-gray-500 mb-2"></vs-icon>
                        <div class="h-12 overflow-hidden mb-2">
                            <span class="file-name text-sm font-medium text-gray-700 text-center break-words w-full">{{ file.name }}</span>
                        </div>
                        <div class="flex mt-2 flex-wrap justify-center">
                            <vs-button class="m-1 w-8 h-8 flex items-center justify-center" size="small" color="success" @click="downloadFile(file)">
                                <vs-icon icon="cloud_download" size="small" class="mx-auto"></vs-icon>
                            </vs-button>
                            <vs-button v-if="$isSuperAdmin()" class="m-1 w-8 h-8 flex items-center justify-center" size="small" color="warning" @click="showFileBranches(file)">
                                <vs-icon icon="call_split" size="small" class="mx-auto"></vs-icon>
                            </vs-button>
                            <vs-button v-if="$isSuperAdmin()" class="m-1 w-8 h-8 flex items-center justify-center" size="small" color="primary" @click="showUpdateFile(file)">
                                <vs-icon icon="edit" size="small" class="mx-auto"></vs-icon>
                            </vs-button>
                            <vs-button v-if="$isSuperAdmin()" class="m-1 w-8 h-8 flex items-center justify-center" size="small" color="danger" @click="confirmDeleteFile(file)">
                                <vs-icon icon="delete" size="small" class="mx-auto"></vs-icon>
                            </vs-button>
                        </div>
                    </div>
                </div>
                <div v-else class="text-center py-8">
                    <vs-icon icon="folder_open" size="large" class="text-gray-400 mb-4"></vs-icon>
                    <p class="text-gray-500">Bu klasör boş.</p>
                </div>
                <div v-if="$isSuperAdmin()" @click="resetNewFile()" 
                     class="file-item flex flex-col items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer min-w-[200px]">
                    <vs-icon icon="add" size="large" class="text-blue-500 mb-2"></vs-icon>
                    <span class="text-sm font-medium text-gray-700">Yeni Dosya Ekle</span>
                </div>
            </div>
        </div>
        <vs-popup class="holamundo" fullscreen title="Dosya Önizleme" :active.sync="showPreview" v-esc="escape">
                <iframe :src="previewUrl" width="100%" height="100%" class="border-0" style="position: absolute;"></iframe>
        </vs-popup>
        <vs-popup class="holamundo" :title="isUpdating ? 'Klasör Güncelle' : 'Yeni Klasör Oluştur'" :active.sync="showFolderPopup">
            <div class="p-4">
                <vs-input v-model="folderModel.name" label="Klasör Adı" class="mb-4" />
                <vs-textarea v-model="folderModel.description" label="Açıklama" class="mb-4" />
                <vs-button @click="isUpdating ? updateFolder() : createFolder()" color="primary" class="w-full">{{ isUpdating ? 'Güncelle' : 'Oluştur' }}</vs-button>
            </div>
        </vs-popup>
        <vs-popup class="holamundo" title="Yeni Dosya Ekle" :active.sync="showCreateFilePopup">
            <div class="p-4">
                <vs-input v-model="newFile.name" label="Dosya Adı" class="mb-4" />
                <vs-textarea v-model="newFile.description" label="Açıklama" class="mb-4" />
                <input id="newFileInput" type="file" @change="handleFileUpload" class="mb-4" />
                <vs-button @click="createFile" color="primary" class="w-full">Dosya Ekle</vs-button>
            </div>
        </vs-popup>
        <vs-popup class="holamundo" title="Dosya Silme Onayı" :active.sync="showDeleteConfirmation">
            <div class="p-4">
                <p class="mb-4">Bu dosyayı silmek istediğinizden emin misiniz?</p>
                <div class="flex justify-end">
                    <vs-button @click="cancelDelete" color="primary" class="mr-2">İptal</vs-button>
                    <vs-button @click="deleteFile" color="danger">Sil</vs-button>
                </div>
            </div>
        </vs-popup>
        <vs-popup class="holamundo" title="Klasör Silme Onayı" :active.sync="showDeleteFolderConfirmation">
            <div class="p-4">
                <p class="mb-4">Bu klasörü silmek istediğinizden emin misiniz?</p>
                <div class="flex justify-end">
                    <vs-button @click="cancelDeleteFolder" color="primary" type="border" class="mr-2">İptal</vs-button>
                    <vs-button @click="deleteFolder" color="danger">Sil</vs-button>
                </div>
            </div>
        </vs-popup>
        <vs-popup class="holamundo" title="Dosya Güncelle" :active.sync="showUpdateFilePopup">
    <div class="p-4">
      <vs-input v-model="updateFileModel.name" label="Dosya Adı" class="mb-4" />
      <vs-textarea v-model="updateFileModel.description" label="Açıklama" class="mb-4" />
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">Dosya Güncelle</label>
        <input type="file" @change="handleUpdateFileUpload" class="block w-full text-sm text-gray-500
          file:mr-4 file:py-2 file:px-4
          file:rounded-full file:border-0
          file:text-sm file:font-semibold
          file:bg-blue-50 file:text-blue-700
          hover:file:bg-blue-100
        "/>
        <p class="mt-2 text-sm text-gray-500">Mevcut dosya: {{ updateFileModel.originalName }}</p>
      </div>
      <vs-button @click="updateFile" color="primary" class="w-full">Güncelle</vs-button>
    </div>
  </vs-popup>
  <vs-popup class="holamundo" :title="`${selectedFileName} - Dosya için yetkili şubeler`" :active.sync="showFileBranchesPopup">
            <div class="p-4">
                <h3 class="text-xl font-semibold mb-4">{{ selectedFileName }} - Dosya için yetkili şubeler</h3>
                <vs-table search :data="fileBranches">
                    <template slot="thead">
                        <vs-th class="w-8/12" sort-key="branchName">
                            Şube Adı
                        </vs-th>
                        <vs-th class="w-4/12" sort-key="isActive">
                            Durum
                        </vs-th>
                    </template>

                    <template slot-scope="{data}">
                        <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                            <vs-td :data="data[indextr].branchName">
                                {{data[indextr].branchName}}
                            </vs-td>

                            <vs-td :data="data[indextr].isActive" vs-align="center">
                                <vs-switch class="ml-2" v-model="data[indextr].isActive" @click="switchChanged(data[indextr])">
                                    <span slot="on">Aktif</span>
                                    <span slot="off">Pasif</span>
                                </vs-switch>
                            </vs-td>
                        </vs-tr>
                    </template>
                </vs-table>
                <div class="mt-4 flex justify-between">
                    <vs-button @click="allBranchesSelected ? deselectAllBranches() : selectAllBranches()" color="primary">
                        {{ allBranchesSelected ? 'Tümünü Kaldır' : 'Tümünü Seç' }}
                    </vs-button>
                    <vs-button @click="saveBranches" color="success">Kaydet</vs-button>
                </div>
            </div>
        </vs-popup>
    </div>
</template>

<script>
import { getFolders, getFolderWithFiles, createFolder, updateFolder, addFile, deleteFile, getFileBranches, addBranchesToFile, deleteFolder, updateFile } from '@/api/Document';

export default {
    data() {
        return {
            folders: [],
            selectedFolderFiles: [],
            selectedFolderName: '',
            selectedFolderId: null,
            showPreview: false,
            previewUrl: null,
            showFolderPopup: false,
            showCreateFilePopup: false,
            showDeleteConfirmation: false,
            showFileBranchesPopup: false,
            fileToDelete: null,
            fileBranches: [],
            selectedFileId: null,
            selectedFileName: '',
            isUpdating: false,
            folderModel: {
                id: null,
                name: '',
                description: ''
            },
            newFile: {
                name: '',
                description: '',
                documentFolderId: null,
                documentFile: null
            },
            folderToDelete: null,
            showDeleteFolderConfirmation: false,
            showUpdateFilePopup: false,
            updateFileModel: {
                id: null,
                name: '',
                description: '',
                documentFile: null,
                originalName: ''
            },
            allBranchesSelected: false,
        };
    },
    mounted() {
        this.fetchFolders();
    },
    methods: {
        async fetchFolders() {
            try {
                const response = await getFolders();
                this.folders = response.data;
            } catch (error) {
                console.error('Error fetching folders:', error);
                // You might want to add error handling UI here
            }
        },
        showUpdateFile(file) {
      this.updateFileModel = {
        id: file.id,
        name: file.name,
        description: file.description,
        documentFile: null,
        originalName: file.url
      };
            this.showUpdateFilePopup = true;
        },
        async fetchFolderFiles(folderId) {
            try {
                const response = await getFolderWithFiles(folderId);
                console.log('Folder files response:', response); // Debug log
                const selectedFolder = this.folders.find(folder => folder.id === folderId);
                this.selectedFolderName = selectedFolder ? selectedFolder.name : '';
                this.selectedFolderId = folderId;
                if (response.data) {
                    this.selectedFolderFiles = response.data;
                    console.log('Selected folder files:', this.selectedFolderFiles); // Debug log
                } else {
                    this.selectedFolderFiles = [];
                    console.log('No files found in the response');
                }
            } catch (error) {
                console.error('Error fetching folder files:', error);
                this.selectedFolderFiles = [];
                // You might want to add error handling UI here
            }
        },
        previewFile(file) {
            this.previewUrl = `${process.env.VUE_APP_ROOT_API}/StaticFiles/images/${file.url}`;
            this.showPreview = true;
            console.log('Preview URL:', this.previewUrl); // Debug log
            console.log('Show Preview:', this.showPreview); // Debug log
        },
        showUpdateFolderPopup(folder) {
            console.log(folder);
            this.isUpdating = true;
            this.folderModel = { ...folder };
            this.showFolderPopup = true;
        },
        showNewFolderPopup() {
            this.isUpdating = false;
            this.folderModel = { id: null, name: '', description: '' };
            this.showFolderPopup = true;
        },
        async createFolder() {
            try {
                await createFolder(this.folderModel);
                this.showFolderPopup = false;
                this.folderModel = { id: null, name: '', description: '' };
                await this.fetchFolders();
            } catch (error) {
                this.$vs.notify({
                  color: 'danger',
                  title: 'Hata !',
                  text: error.response.data.Errors[0].Message
                });
            }
        },
        async updateFolder() {
            try {
                await updateFolder(this.folderModel);
                this.showFolderPopup = false;
                this.folderModel = { id: null, name: '', description: '' };
                this.isUpdating = false;
                await this.fetchFolders();
            } catch (error) {
                this.$vs.notify({
                  color: 'danger',
                  title: 'Hata !',
                  text: error.response.data.Errors[0].Message
                });
            }
        },
        handleFileUpload(event) {
            this.newFile.documentFile = event.target.files[0];
        },
        handleUpdateFileUpload(event) {
            this.updateFileModel.documentFile = event.target.files[0];
        },
        async createFile() {
            try {
                debugger;
                console.log(this.newFile);
                this.newFile.documentFolderId = this.selectedFolderId;
                const formData = new FormData();
                formData.append('name', this.newFile.name);
                formData.append('description', this.newFile.description);
                formData.append('documentFolderId', this.newFile.documentFolderId);
                formData.append('documentFile', this.newFile.documentFile);

                console.log('başladı');
                await addFile(formData);
                console.log('bitti');
                this.showCreateFilePopup = false;
                this.newFile = { name: '', description: '', documentFolderId: null, documentFile: null };
                await this.fetchFolderFiles(this.selectedFolderId);
            } catch (error) {
                console.log(error);
                this.$vs.notify({
                  color: 'danger',
                  title: 'Hata !',
                  text: error.response.data.Errors[0].Message
                });
            }
        },
        async updateFile() {
            try {
                const formData = new FormData();
                formData.append('id', this.updateFileModel.id);
                formData.append('name', this.updateFileModel.name);
                formData.append('description', this.updateFileModel.description);
                if (this.updateFileModel.documentFile) {
                    formData.append('documentFile', this.updateFileModel.documentFile);
                }
                await updateFile(formData);
                this.showUpdateFilePopup = false;
                this.updateFileModel = { id: null, name: '', description: '', documentFile: null, originalName: '' };
                await this.fetchFolderFiles(this.selectedFolderId);
                this.$vs.notify({
                    color: 'success',
                    title: 'Başarılı',
                    text: 'Dosya başarıyla güncellendi.'
                });
            } catch (error) {
                this.$vs.notify({
                  color: 'danger',
                  title: 'Hata !',
                  text: error.response.data.Errors[0].Message
                });
            }
        },
        confirmDeleteFile(file) {
            this.fileToDelete = file;
            this.showDeleteConfirmation = true;
        },
        cancelDelete() {
            this.fileToDelete = null;
            this.showDeleteConfirmation = false;
        },
        confirmDeleteFolder(folder) {
            console.log(folder);
            this.folderToDelete = folder;
            this.showDeleteFolderConfirmation = true;
        },
        cancelDeleteFolder() {
            this.folderToDelete = null;
            this.showDeleteFolderConfirmation = false;
        },
        async deleteFile() {
            try {
                await deleteFile(this.fileToDelete.id);
                this.showDeleteConfirmation = false;
                this.fileToDelete = null;
                await this.fetchFolderFiles(this.selectedFolderId);
                this.$vs.notify({
                    color: 'success',
                    title: 'Başarılı',
                    text: 'Dosya başarıyla silindi.'
                });
            } catch (error) {
                console.error('Error deleting file:', error);
                this.$vs.notify({
                    color: 'danger',
                    title: 'Hata !',
                    text: error.response && error.response.data && error.response.data.Errors && error.response.data.Errors[0] && error.response.data.Errors[0].Message ? error.response.data.Errors[0].Message : 'Dosya silinirken bir hata oluştu.'
                });
            }
        },
        async deleteFolder() {
            try {
                console.log(this.folderToDelete);
                await deleteFolder(this.folderToDelete.id);
                this.showDeleteFolderConfirmation = false;
                this.folderToDelete = null;
                await this.fetchFolders();
                this.$vs.notify({
                    color: 'success',
                    title: 'Başarılı',
                    text: 'Klasör başarıyla silindi.'
                });
            } catch (error) {
                console.error('Error deleting folder:', error);
                this.$vs.notify({
                    color: 'danger',
                    title: 'Hata !',
                    text: error.response && error.response.data && error.response.data.Errors && error.response.data.Errors[0] && error.response.data.Errors[0].Message ? error.response.data.Errors[0].Message : 'Dosya silinirken bir hata oluştu.'
                });
            }
        },
        downloadFile(file) {
            const downloadUrl = `${process.env.VUE_APP_ROOT_API}/StaticFiles/images/${file.url}`;
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Dosya adındaki noktalama işaretlerini kaldır veya değiştir
            const safeFileName = file.name.replace(/[<>:"/\\|?*.]/g, '_').replace(/\s+/g, '_');
            
            link.setAttribute('download', safeFileName);
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        async showFileBranches(file) {
            try {
                const response = await getFileBranches(file.id);
                this.fileBranches = response.data;
                this.selectedFileId = file.id;
                this.selectedFileName = file.name;
                this.showFileBranchesPopup = true;
                this.checkAllBranchesSelected();
            } catch (error) {
                console.error('Error fetching file branches:', error);
                this.$vs.notify({
                    color: 'danger',
                    title: 'Hata !',
                    text: 'Dosya şubeleri alınırken bir hata oluştu.'
                });
            }
        },
        selectAllBranches() {
            this.fileBranches.forEach(branch => {
                branch.isActive = true;
            });
            this.allBranchesSelected = true;
        },
        deselectAllBranches() {
            this.fileBranches.forEach(branch => {
                branch.isActive = false;
            });
            this.allBranchesSelected = false;
        },
        checkAllBranchesSelected() {
            this.allBranchesSelected = this.fileBranches.every(branch => branch.isActive);
        },
        switchChanged(branch) {
            this.checkAllBranchesSelected();
        },
        async saveBranches() {
            try {
                const selectedBranches = this.fileBranches.filter(branch => branch.isActive).map(branch => branch.branchId);
                const addBranchesToFileDto = {
                    Branches: selectedBranches,
                    FileId: this.selectedFileId
                };
                await addBranchesToFile(addBranchesToFileDto);
                this.showFileBranchesPopup = false;
                this.$vs.notify({
                    color: 'success',
                    title: 'Başarılı',
                    text: 'Dosya şubeleri başarıyla kaydedildi.'
                });
            } catch (error) {
                console.error('Error saving file branches:', error);
                this.$vs.notify({
                    color: 'danger',
                    title: 'Hata !',
                    text: 'Dosya şubeleri kaydedilirken bir hata oluştu.'
                });
            }
        },
        resetNewFile() {
            this.newFile = {
                name: '',
                description: '',
                documentFolderId: null,
                documentFile: null
            };
            document.getElementById('newFileInput').value = '';
            this.showCreateFilePopup = true;
        }
    }
}
</script>

<style scoped>
.folder-item:hover .folder-name {
    color: #2563eb; /* This is the equivalent of Tailwind's text-blue-600 */
}
.file-item:hover .file-name {
    color: #2563eb;
}
.vs-icon.small {
    font-size: 1rem !important;
    height: 1rem !important;
    width: 1rem !important;
}
</style>